import React from "react";
import { useTranslation } from "react-i18next";
import { Badge } from "@/components/ui/badge";
import type { SubscriptionPlan } from "@/types/subscription-plan";
import { TenantPlanFeatureBadges } from "./TenantPlanFeatureBadges";
import { formatMoney, normalizeSubscriptionPlanFeature } from "./utils";

interface TenantSelectedPlanPreviewProps {
  plan: SubscriptionPlan | null;
}

export const TenantSelectedPlanPreview = ({
  plan,
}: TenantSelectedPlanPreviewProps): React.JSX.Element | null => {
  const { t } = useTranslation();

  if (plan === null) {
    return null;
  }

  const normalizedFeatures = plan.features.map(
    normalizeSubscriptionPlanFeature,
  );

  return (
    <div className="mt-4 flex flex-col gap-3 rounded-lg border border-border bg-background p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm font-semibold text-zinc-950">{plan.title}</p>
          <p className="mt-1 text-sm text-muted-foreground">
            {formatMoney(plan.price, plan.currency)}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {plan.isDefault ? (
            <Badge variant="new_secondary">
              {t("subscriptions.tenantManagement.planDetails.defaultPlan")}
            </Badge>
          ) : null}
          {plan.isCustom ? (
            <Badge variant="new_secondary">
              {t("subscriptions.tenantManagement.planDetails.customPlan")}
            </Badge>
          ) : null}
          {plan.enableCoreFeatures ? (
            <Badge variant="new_default">
              {t("subscriptions.tenantManagement.planDetails.coreIncluded")}
            </Badge>
          ) : null}
        </div>
      </div>
      <div className="flex flex-col gap-2">
        <p className="text-xs uppercase tracking-[0.12em] text-muted-foreground">
          {t("subscriptions.tenantManagement.planDetails.includedFeatures")}
        </p>
        <TenantPlanFeatureBadges
          features={normalizedFeatures}
          enableCoreFeatures={plan.enableCoreFeatures}
          planTitle={plan.title}
        />
      </div>
    </div>
  );
};
